/**
 * Intelligent Link Prefetching (Phase III Performance Optimization)
 *
 * Prefetches pages when users hover over links, making navigation feel instant.
 * Features:
 * - 65ms hover delay to prevent accidental prefetches
 * - Connection-aware (skips on slow connections)
 * - Budget limit (5 concurrent prefetches)
 * - Touch support for mobile
 * - Intersection Observer for priority links
 *
 * Expected impact: Navigation feels instant (perceived load time: 0ms)
 *
 * @version 1.0.0
 */

(function() {
  'use strict';

  // Configuration
  const HOVER_DELAY = 65; // ms - prevents accidental hovers
  const MAX_PREFETCHES = 5; // Concurrent prefetch limit
  const TOUCH_DELAY = 200; // ms - delay for touch devices

  // State
  let prefetchedUrls = new Set();
  let pendingPrefetches = 0;
  let hoverTimer = null;

  /**
   * Check if we should prefetch based on connection quality
   */
  function shouldPrefetch() {
    // Don't prefetch if user has data saver enabled
    if (navigator.connection && navigator.connection.saveData) {
      return false;
    }

    // Don't prefetch on slow connections
    if (navigator.connection && navigator.connection.effectiveType) {
      const connectionType = navigator.connection.effectiveType;
      if (connectionType === '2g' || connectionType === 'slow-2g') {
        return false;
      }
    }

    // Don't prefetch if we've hit the concurrent limit
    if (pendingPrefetches >= MAX_PREFETCHES) {
      return false;
    }

    return true;
  }

  /**
   * Check if URL should be prefetched
   */
  function isValidUrl(url) {
    // Skip if already prefetched
    if (prefetchedUrls.has(url)) {
      return false;
    }

    // Skip external links
    if (!url.startsWith(window.location.origin)) {
      return false;
    }

    // Skip cart, checkout, my-account (dynamic pages)
    if (url.match(/\/(cart|checkout|my-account)\//)) {
      return false;
    }

    // Skip admin and login pages
    if (url.match(/\/wp-admin\/|\/wp-login\.php/)) {
      return false;
    }

    return true;
  }

  /**
   * Prefetch a URL
   */
  function prefetchUrl(url) {
    if (!shouldPrefetch() || !isValidUrl(url)) {
      return;
    }

    // Mark as prefetched
    prefetchedUrls.add(url);
    pendingPrefetches++;

    // Create link tag for prefetch
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    link.as = 'document';

    // Track completion
    link.onload = link.onerror = function() {
      pendingPrefetches--;
    };

    // Add to document
    document.head.appendChild(link);

    console.log('[Prefetch] Prefetching:', url);
  }

  /**
   * Handle mouse enter on link
   */
  function handleMouseEnter(e) {
    const link = e.target.closest('a');
    if (!link || !link.href) return;

    // Clear any existing timer
    if (hoverTimer) {
      clearTimeout(hoverTimer);
    }

    // Set timer for hover delay
    hoverTimer = setTimeout(function() {
      prefetchUrl(link.href);
    }, HOVER_DELAY);
  }

  /**
   * Handle mouse leave on link
   */
  function handleMouseLeave() {
    // Cancel pending prefetch
    if (hoverTimer) {
      clearTimeout(hoverTimer);
      hoverTimer = null;
    }
  }

  /**
   * Handle touch start on link (for mobile)
   */
  function handleTouchStart(e) {
    const link = e.target.closest('a');
    if (!link || !link.href) return;

    // Prefetch on touch with small delay
    setTimeout(function() {
      prefetchUrl(link.href);
    }, TOUCH_DELAY);
  }

  /**
   * Observe priority links that come into viewport
   */
  function observePriorityLinks() {
    if (!('IntersectionObserver' in window)) {
      return;
    }

    const observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          const link = entry.target;
          if (link.href) {
            prefetchUrl(link.href);
          }
          observer.unobserve(link);
        }
      });
    }, {
      rootMargin: '50px' // Start prefetching when link is 50px from viewport
    });

    // Observe all links with data-prefetch-priority attribute
    document.querySelectorAll('a[data-prefetch-priority]').forEach(function(link) {
      observer.observe(link);
    });
  }

  /**
   * Initialize prefetching
   */
  function init() {
    // Check browser support
    if (!('requestIdleCallback' in window)) {
      // Fallback for browsers without requestIdleCallback
      window.requestIdleCallback = function(cb) {
        return setTimeout(cb, 1);
      };
    }

    // Wait for page to be idle before setting up listeners
    requestIdleCallback(function() {
      // Add hover listeners to all internal links
      document.addEventListener('mouseover', handleMouseEnter, {passive: true});
      document.addEventListener('mouseout', handleMouseLeave, {passive: true});

      // Add touch listeners for mobile
      document.addEventListener('touchstart', handleTouchStart, {passive: true});

      // Observe priority links
      observePriorityLinks();

      console.log('[Prefetch] Initialized successfully');
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
